import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, Users, ChevronRight, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import type { Proposal } from '../../types/proposal';
import { Modal } from '../ui/Modal';
import { useProposalStore } from '../../stores/proposalStore';
import { toast } from 'react-hot-toast';

interface ProposalListProps {
  proposals: Proposal[];
}

export function ProposalList({ proposals }: ProposalListProps) {
  const navigate = useNavigate();
  const { deleteProposal } = useProposalStore();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleProposalClick = (proposalId: string) => {
    navigate(`/proposals/${proposalId}`);
  };

  const handleDeleteClick = (e: React.MouseEvent, proposal: Proposal) => {
    e.stopPropagation(); // Prevent navigation
    setSelectedProposal(proposal);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedProposal) return;

    setDeleting(true);
    try {
      await deleteProposal(selectedProposal.id);
      toast.success('Proposal deleted successfully');
      setShowDeleteModal(false);
      setSelectedProposal(null);
    } catch (error) {
      console.error('Failed to delete proposal:', error);
      toast.error('Failed to delete proposal');
    } finally {
      setDeleting(false);
    }
  };

  if (proposals.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No proposals</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new proposal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {proposals.map((proposal) => (
          <div
            key={proposal.id}
            onClick={() => handleProposalClick(proposal.id)}
            className="cursor-pointer rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="rounded-lg bg-purple-100 p-3">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{proposal.title}</h3>
                  <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Calendar className="mr-1.5 h-4 w-4" />
                      Due {new Date(proposal.deadline).toLocaleDateString()}
                    </span>
                    <span className="flex items-center">
                      <Users className="mr-1.5 h-4 w-4" />
                      {proposal.team.length} members
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  proposal.status === 'draft'
                    ? 'bg-gray-100 text-gray-800'
                    : proposal.status === 'in_review'
                    ? 'bg-yellow-100 text-yellow-800'
                    : proposal.status === 'submitted'
                    ? 'bg-blue-100 text-blue-800'
                    : proposal.status === 'awarded'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {proposal.status.replace('_', ' ').charAt(0).toUpperCase() + proposal.status.slice(1)}
                </span>
                <button
                  onClick={(e) => handleDeleteClick(e, proposal)}
                  className="rounded-full p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Progress</span>
                <span className="font-medium text-gray-900">
                  {Math.round((proposal.sections.filter(s => s.status === 'completed').length / proposal.sections.length) * 100)}%
                </span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full bg-purple-600 transition-all duration-500"
                  style={{
                    width: `${(proposal.sections.filter(s => s.status === 'completed').length / proposal.sections.length) * 100}%`
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedProposal(null);
        }}
        title="Delete Proposal"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 rounded-lg bg-red-50 p-4">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div>
              <h3 className="text-sm font-medium text-red-800">
                Are you sure you want to delete this proposal?
              </h3>
              <p className="mt-1 text-sm text-red-700">
                This action cannot be undone. The proposal and all associated data will be permanently deleted.
              </p>
            </div>
          </div>

          {selectedProposal && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-medium text-gray-900">{selectedProposal.title}</h4>
              <p className="mt-1 text-sm text-gray-500">
                Due: {new Date(selectedProposal.deadline).toLocaleDateString()}
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedProposal(null);
              }}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Proposal
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}